import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  X,
  Save,
  User,
  Shield,
  Plane,
  Car,
  HeartPulse,
  Briefcase,
  GraduationCap,
  Users,
  Home,
  Sparkles,
  Paperclip,
  Trash2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  Check,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Globe,
  FileText,
  AlertCircle,
  Share2,
  Download,
  Plus,
  Clock,
  ExternalLink,
} from 'lucide-react';
import { IDENTITY_TEMPLATES } from './IdentityTemplatesModal';

const IDENTITY_TYPE_ICONS = {
  personal: User,
  government_id: Shield,
  passport: Plane,
  driving_licence: Car,
  health_id: HeartPulse,
  work_id: Briefcase,
  student_id: GraduationCap,
  family_member: Users,
  address_id: Home,
  custom: Sparkles,
};

export default function IdentityEditorModal({
  open,
  identity, // null if new identity, or existing identity object
  categories = [],
  onClose,
  onSave,
  hasEditPermission = true,
  onOpenTemplates,
}) {
  const [activeTab, setActiveTab] = useState('details'); // 'details' | 'attachments' | 'security'
  
  // Basic info
  const [title, setTitle] = useState('');
  const [identityType, setIdentityType] = useState('personal');
  const [category, setCategory] = useState('Personal');
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  // Personal Fields
  const [fullName, setFullName] = useState('');
  const [dob, setDob] = useState('');
  const [nationality, setNationality] = useState('');
  const [gender, setGender] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');

  // Address
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [stateProvince, setStateProvince] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');

  // Document Details
  const [documentNumber, setDocumentNumber] = useState('');
  const [showDocNumber, setShowDocNumber] = useState(false);
  const [docNumberCopied, setDocNumberCopied] = useState(false);
  const [issueDate, setIssueDate] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [issuingAuthority, setIssuingAuthority] = useState('');

  // Notes & Attachments
  const [notes, setNotes] = useState('');
  const [attachments, setAttachments] = useState([]);

  // Security & Lock
  const [isLocked, setIsLocked] = useState(false);
  const [lockPin, setLockPin] = useState('');
  const [showPinInput, setShowPinInput] = useState(false);

  // Sharing & Collaborators
  const [shareRecipientEmail, setShareRecipientEmail] = useState('');
  const [sharePermission, setSharePermission] = useState('VIEW'); // 'VIEW' | 'EDIT'
  const [sharesList, setSharesList] = useState([]);

  // Status & Timers
  const [autosaveStatus, setAutosaveStatus] = useState('saved'); // 'saved' | 'saving' | 'dirty'
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const autoHideTimerRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const initialLoadRef = useRef(false);

  // Initialize form state
  useEffect(() => {
    if (!open) {
      initialLoadRef.current = false;
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      return;
    }

    if (identity) {
      setTitle(identity.title || '');
      setIdentityType(identity.identityType || 'personal');
      setCategory(identity.category || 'Personal');
      setTags(Array.isArray(identity.tags) ? identity.tags : []);

      // Extract fields from identity object
      const f = identity.fields || identity;
      setFullName(f.fullName || '');
      setDob(f.dob || '');
      setNationality(f.nationality || '');
      setGender(f.gender || '');
      setPhone(f.phone || '');
      setEmail(f.email || '');

      const addr = f.address || {};
      setStreet(addr.street || '');
      setCity(addr.city || '');
      setStateProvince(addr.state || addr.stateProvince || '');
      setPostalCode(addr.postalCode || '');
      setCountry(addr.country || '');

      setDocumentNumber(f.documentNumber || '');
      setIssueDate(f.issueDate || '');
      setExpiryDate(f.expiryDate || '');
      setIssuingAuthority(f.issuingAuthority || '');

      setNotes(f.notes || identity.notes || '');
      setAttachments(Array.isArray(identity.attachments) ? identity.attachments : []);

      setIsLocked(Boolean(identity.isLocked));
      setLockPin(identity.lockPin || '');
      setShowPinInput(Boolean(identity.isLocked));

      setSharesList(Array.isArray(identity.shares) ? identity.shares : []);
      setAutosaveStatus('saved');
    } else {
      setTitle('');
      setIdentityType('personal');
      setCategory(categories[0]?.name || 'Personal');
      setTags([]);
      setFullName('');
      setDob('');
      setNationality('');
      setGender('');
      setPhone('');
      setEmail('');
      setStreet('');
      setCity('');
      setStateProvince('');
      setPostalCode('');
      setCountry('');
      setDocumentNumber('');
      setIssueDate('');
      setExpiryDate('');
      setIssuingAuthority('');
      setNotes('');
      setAttachments([]);
      setIsLocked(false);
      setLockPin('');
      setShowPinInput(false);
      setSharesList([]);
      setAutosaveStatus('saved');
    }

    setShowDocNumber(false);
    setError('');
    setActiveTab('details');
    initialLoadRef.current = true;
  }, [open, identity, categories]);

  // Sensitive field reveal with auto-hide timer (15 seconds)
  const toggleDocNumberVisibility = () => {
    if (!showDocNumber) {
      setShowDocNumber(true);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = setTimeout(() => {
        setShowDocNumber(false);
      }, 15000);
    } else {
      setShowDocNumber(false);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    }
  };

  const copyToClipboard = (text, setCopiedState) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedState(true);
    setTimeout(() => setCopiedState(false), 2000);
  };

  // Expiry status calculation
  const getExpiryStatus = () => {
    if (!expiryDate) return null;
    const now = new Date();
    const expiry = new Date(expiryDate);
    if (isNaN(expiry.getTime())) return null;

    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'expired', label: `Expired ${Math.abs(diffDays)}d ago`, color: '#ef4444' };
    }
    if (diffDays <= 30) {
      return { status: 'expiring_soon', label: `Expiring in ${diffDays} days!`, color: '#f59e0b' };
    }
    if (diffDays <= 90) {
      return { status: 'expiring_upcoming', label: `Expiring in ${diffDays} days`, color: '#3b82f6' };
    }
    return { status: 'valid', label: `Valid (${diffDays} days left)`, color: '#10b981' };
  };

  const expiryBadge = getExpiryStatus();

  // Tag management
  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const clean = tagInput.trim().toLowerCase().replace(/^#/, '');
      if (clean && !tags.includes(clean)) {
        setTags([...tags, clean]);
      }
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  // Attachments management
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    files.forEach((file) => {
      // Max 10MB per document
      if (file.size > 10 * 1024 * 1024) {
        setError(`File ${file.name} exceeds 10MB limit.`);
        return;
      }

      const reader = new FileReader();
      reader.onload = () => {
        const newAttachment = {
          id: `att_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
          name: file.name,
          size: file.size,
          type: file.type,
          dataUrl: reader.result,
          uploadedAt: new Date().toISOString(),
        };
        setAttachments((prev) => [...prev, newAttachment]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = '';
  };

  const handleRemoveAttachment = (attId) => {
    setAttachments((prev) => prev.filter((a) => a.id !== attId));
  };

  // Collaborator sharing
  const handleAddShare = (e) => {
    e.preventDefault();
    if (!shareRecipientEmail || !shareRecipientEmail.includes('@')) {
      setError('Please enter a valid email to share with.');
      return;
    }
    if (sharesList.some((s) => s.email.toLowerCase() === shareRecipientEmail.toLowerCase())) {
      setError('This user is already added.');
      return;
    }

    const newShare = {
      id: `share_${Date.now()}`,
      email: shareRecipientEmail.trim().toLowerCase(),
      permission: sharePermission,
      sharedAt: new Date().toISOString(),
    };
    setSharesList([...sharesList, newShare]);
    setShareRecipientEmail('');
    setError('');
  };

  const handleRemoveShare = (shareId) => {
    setSharesList(sharesList.filter((s) => s.id !== shareId));
  };

  // Apply template
  const handleApplyTemplate = (tmpl) => {
    if (!tmpl) return;
    setTitle(tmpl.title || tmpl.defaultTitle || '');
    setIdentityType(tmpl.identityType || 'personal');
    setCategory(tmpl.category || 'Personal');
    if (Array.isArray(tmpl.tags)) {
      setTags([...tmpl.tags]);
    }
    if (tmpl.fields) {
      setFullName(tmpl.fields.fullName || '');
      setDob(tmpl.fields.dob || '');
      setNationality(tmpl.fields.nationality || '');
      setGender(tmpl.fields.gender || '');
      setPhone(tmpl.fields.phone || '');
      setEmail(tmpl.fields.email || '');
      if (tmpl.fields.address) {
        setStreet(tmpl.fields.address.street || '');
        setCity(tmpl.fields.address.city || '');
        setStateProvince(tmpl.fields.address.state || '');
        setPostalCode(tmpl.fields.address.postalCode || '');
        setCountry(tmpl.fields.address.country || '');
      }
      setDocumentNumber(tmpl.fields.documentNumber || '');
      setIssueDate(tmpl.fields.issueDate || '');
      setExpiryDate(tmpl.fields.expiryDate || '');
      setIssuingAuthority(tmpl.fields.issuingAuthority || '');
      setNotes(tmpl.fields.notes || '');
    }
  };

  // Submit & Save
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      setError('Identity title is required.');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const identityData = {
        title: title.trim(),
        identityType,
        category,
        tags,
        fields: {
          fullName: fullName.trim(),
          dob,
          nationality: nationality.trim(),
          gender: gender.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: {
            street: street.trim(),
            city: city.trim(),
            state: stateProvince.trim(),
            postalCode: postalCode.trim(),
            country: country.trim(),
          },
          documentNumber: documentNumber.trim(),
          issueDate,
          expiryDate,
          issuingAuthority: issuingAuthority.trim(),
          notes: notes.trim(),
        },
        attachments,
        isLocked,
        lockPin: isLocked ? lockPin : '',
        shares: sharesList,
        updated_at: new Date().toISOString(),
      };

      await onSave(identityData);
      onClose();
    } catch (err) {
      console.error('Failed to save identity:', err);
      setError(err.message || 'Error encrypting and saving identity record.');
    } finally {
      setSaving(false);
    }
  };

  const IconComponent = IDENTITY_TYPE_ICONS[identityType] || User;

  if (!open) return null;

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-editor-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={identity ? 'Edit Identity' : 'Create New Identity'}
      >
        {/* Header */}
        <div className="card-drawer-header modal-header identity-editor-header">
          <div className="identity-editor-title-group">
            <div className="identity-header-avatar">
              <IconComponent size={22} />
            </div>
            <div>
              <h2 className="modal-title">
                {identity ? 'Edit Identity' : 'Create New Identity'}
              </h2>
              <p className="modal-subtitle">
                Zero-knowledge encrypted identity credentials, documents & personal data
              </p>
            </div>
          </div>

          <div className="identity-editor-header-actions">
            {!identity && onOpenTemplates && (
              <button
                type="button"
                className="btn btn-secondary btn-sm identity-templates-header-btn"
                onClick={onOpenTemplates}
                title="Choose from 10 identity templates"
              >
                <Sparkles size={14} className="text-primary" />
                <span className="identity-templates-btn-text">Templates</span>
              </button>
            )}
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close dialog"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger" style={{ margin: '12px 24px 0' }}>
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Tab Navigation */}
        <div className="identity-editor-tabs-bar">
          <button
            type="button"
            className={`identity-editor-tab-btn ${activeTab === 'details' ? 'active' : ''}`}
            onClick={() => setActiveTab('details')}
          >
            <User size={15} />
            <span>Identity Details</span>
          </button>
          <button
            type="button"
            className={`identity-editor-tab-btn ${activeTab === 'attachments' ? 'active' : ''}`}
            onClick={() => setActiveTab('attachments')}
          >
            <Paperclip size={15} />
            <span>Documents & Attachments ({attachments.length})</span>
          </button>
          <button
            type="button"
            className={`identity-editor-tab-btn ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            <Lock size={15} />
            <span>Security & Sharing {sharesList.length > 0 ? `(${sharesList.length})` : ''}</span>
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="identity-editor-form card-drawer-form-wrapper">
          <div className="card-drawer-body modal-body identity-editor-body">
            {activeTab === 'details' && (
              <div className="identity-form-sections">
                {/* Section 1: Record Metadata */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Identity Record Information</h4>
                  <div className="grid-2-col">
                    <div className="form-group">
                      <label className="form-label">
                        Identity Title <span className="text-danger">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. Passport: Nikhil Sharma or Primary Driver's License"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Identity Type</label>
                      <select
                        className="form-input"
                        value={identityType}
                        onChange={(e) => setIdentityType(e.target.value)}
                      >
                        <option value="personal">Personal Identity</option>
                        <option value="government_id">Government ID / SSN</option>
                        <option value="passport">Passport</option>
                        <option value="driving_licence">Driving Licence</option>
                        <option value="health_id">Health / Insurance ID</option>
                        <option value="work_id">Work Identity</option>
                        <option value="student_id">Student Identity</option>
                        <option value="family_member">Family Member</option>
                        <option value="address_id">Address Identity</option>
                        <option value="custom">Custom Identity</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid-2-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Category</label>
                      <select
                        className="form-input"
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                      >
                        <option value="Personal">Personal</option>
                        <option value="Family">Family</option>
                        <option value="Work">Work</option>
                        <option value="Legal">Legal</option>
                        <option value="Medical">Medical</option>
                        {categories
                          .filter(
                            (c) =>
                              !['Personal', 'Family', 'Work', 'Legal', 'Medical'].includes(c.name)
                          )
                          .map((c) => (
                            <option key={c.id || c.name} value={c.name}>
                              {c.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Tags (Press Enter to add)</label>
                      <div className="tags-input-wrapper">
                        <div className="tags-list">
                          {tags.map((t) => (
                            <span key={t} className="tag-chip">
                              #{t}
                              <button
                                type="button"
                                className="tag-remove-btn"
                                onClick={() => handleRemoveTag(t)}
                              >
                                &times;
                              </button>
                            </span>
                          ))}
                        </div>
                        <input
                          type="text"
                          className="tags-bare-input"
                          placeholder={tags.length === 0 ? 'Type tag and hit Enter...' : ''}
                          value={tagInput}
                          onChange={(e) => setTagInput(e.target.value)}
                          onKeyDown={handleAddTag}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Personal Profile */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Personal Profile Details</h4>
                  <div className="grid-2-col">
                    <div className="form-group">
                      <label className="form-label">Full Legal Name</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="As shown on official documents"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Date of Birth</label>
                      <input
                        type="date"
                        className="form-input"
                        value={dob}
                        onChange={(e) => setDob(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="grid-3-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Nationality</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. United States"
                        value={nationality}
                        onChange={(e) => setNationality(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Gender</label>
                      <select
                        className="form-input"
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                      >
                        <option value="">Select Gender</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                        <option value="Non-Binary">Non-Binary</option>
                        <option value="Prefer not to say">Prefer not to say</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Phone Number</label>
                      <input
                        type="tel"
                        className="form-input"
                        placeholder="+1 (555) 000-0000"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="form-group" style={{ marginTop: '12px' }}>
                    <label className="form-label">Contact Email Address</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="name@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                </div>

                {/* Section 3: Document Numbers & Validity */}
                <div className="identity-form-section">
                  <div className="identity-section-header-flex">
                    <h4 className="identity-section-heading">Official Document & Expiry</h4>
                    {expiryBadge && (
                      <span
                        className="identity-expiry-status-pill"
                        style={{
                          backgroundColor: `${expiryBadge.color}18`,
                          color: expiryBadge.color,
                          borderColor: `${expiryBadge.color}40`,
                        }}
                      >
                        <Clock size={13} />
                        {expiryBadge.label}
                      </span>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">
                      Document / Identification Number
                      <span className="identity-field-subtext">
                        (Masked by default. Click eye to reveal for 15 seconds)
                      </span>
                    </label>
                    <div className="sensitive-input-group">
                      <input
                        type={showDocNumber ? 'text' : 'password'}
                        className="form-input sensitive-input"
                        placeholder="e.g. Passport Number, SSN, DL-123456"
                        value={documentNumber}
                        onChange={(e) => setDocumentNumber(e.target.value)}
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        className="sensitive-action-btn"
                        onClick={toggleDocNumberVisibility}
                        title={showDocNumber ? 'Hide value' : 'Reveal for 15 seconds'}
                      >
                        {showDocNumber ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <button
                        type="button"
                        className="sensitive-action-btn"
                        onClick={() => copyToClipboard(documentNumber, setDocNumberCopied)}
                        title="Copy document number"
                        disabled={!documentNumber}
                      >
                        {docNumberCopied ? <Check size={16} className="text-success" /> : <Copy size={16} />}
                      </button>
                    </div>
                  </div>

                  <div className="grid-3-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">Issuing Authority / Agency</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="e.g. State Dept, California DMV, NHS"
                        value={issuingAuthority}
                        onChange={(e) => setIssuingAuthority(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Issue Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={issueDate}
                        onChange={(e) => setIssueDate(e.target.value)}
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Expiry Date</label>
                      <input
                        type="date"
                        className="form-input"
                        value={expiryDate}
                        onChange={(e) => setExpiryDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 4: Address Details */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Residential / Registered Address</h4>
                  <div className="form-group">
                    <label className="form-label">Street Address</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="e.g. 100 Main Street, Apt 4B"
                      value={street}
                      onChange={(e) => setStreet(e.target.value)}
                    />
                  </div>

                  <div className="grid-4-col" style={{ marginTop: '12px' }}>
                    <div className="form-group">
                      <label className="form-label">City</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="San Francisco"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">State / Province</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="CA"
                        value={stateProvince}
                        onChange={(e) => setStateProvince(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">ZIP / Postal Code</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="94105"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Country</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="United States"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Section 5: Encrypted Notes */}
                <div className="identity-form-section">
                  <h4 className="identity-section-heading">Confidential Notes</h4>
                  <div className="form-group">
                    <textarea
                      className="form-input identity-notes-textarea"
                      rows={4}
                      placeholder="Enter any additional confidential details, emergency contacts, or renewal requirements..."
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Attachments Tab */}
            {activeTab === 'attachments' && (
              <div className="identity-attachments-tab-content">
                <div className="identity-attachments-header">
                  <div>
                    <h3 className="identity-section-heading">Identity Document Scans & Files</h3>
                    <p className="text-secondary text-sm">
                      Upload scanned copies of passports, driver licenses, or verification certificates.
                      Files are client-side encrypted before storage.
                    </p>
                  </div>

                  <label className="btn btn-secondary btn-sm identity-upload-btn">
                    <Paperclip size={15} />
                    <span>Upload Document Scan</span>
                    <input
                      type="file"
                      multiple
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                      accept="image/*,.pdf,.doc,.docx"
                    />
                  </label>
                </div>

                {attachments.length === 0 ? (
                  <div className="identity-empty-attachments">
                    <FileText size={44} opacity={0.25} />
                    <p>No document scans attached yet.</p>
                    <span className="text-secondary text-sm">
                      Attach PDFs, JPEG, or PNG scans for secure offline and travel verification.
                    </span>
                  </div>
                ) : (
                  <div className="identity-attachments-grid">
                    {attachments.map((att) => {
                      const isImage = att.type?.startsWith('image/');
                      const sizeKb = (att.size / 1024).toFixed(1);
                      return (
                        <div key={att.id} className="identity-attachment-card">
                          <div className="identity-attachment-preview">
                            {isImage && att.dataUrl ? (
                              <img src={att.dataUrl} alt={att.name} className="identity-attachment-thumb" />
                            ) : (
                              <FileText size={36} className="text-primary" />
                            )}
                          </div>
                          <div className="identity-attachment-info">
                            <span className="identity-attachment-name" title={att.name}>
                              {att.name}
                            </span>
                            <span className="identity-attachment-meta">
                              {sizeKb} KB • {new Date(att.uploadedAt).toLocaleDateString()}
                            </span>
                          </div>
                          <div className="identity-attachment-actions">
                            {att.dataUrl && (
                              <a
                                href={att.dataUrl}
                                download={att.name}
                                className="btn-icon btn-sm"
                                title="Download decrypted file"
                              >
                                <Download size={14} />
                              </a>
                            )}
                            <button
                              type="button"
                              className="btn-icon btn-sm btn-icon-danger"
                              title="Delete file"
                              onClick={() => handleRemoveAttachment(att.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Security & Sharing Tab */}
            {activeTab === 'security' && (
              <div className="identity-security-tab-content">
                {/* Security PIN Lock */}
                <div className="identity-security-card">
                  <div className="identity-security-card-header">
                    <div className="identity-security-icon-box">
                      <Shield size={20} className="text-primary" />
                    </div>
                    <div className="identity-security-info">
                      <h4>Identity Lock PIN</h4>
                      <p className="text-secondary text-sm">
                        Require an extra security PIN before revealing document numbers and sensitive scans.
                      </p>
                    </div>
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={isLocked}
                        onChange={(e) => {
                          setIsLocked(e.target.checked);
                          setShowPinInput(e.target.checked);
                        }}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>

                  {isLocked && (
                    <div className="identity-lock-pin-form">
                      <div className="form-group">
                        <label className="form-label">Set 4 to 8 Digit Security PIN</label>
                        <input
                          type="password"
                          className="form-input"
                          maxLength={8}
                          placeholder="••••"
                          value={lockPin}
                          onChange={(e) => setLockPin(e.target.value)}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Collaboration & Sharing */}
                <div className="identity-sharing-section">
                  <h4 className="identity-section-heading">Authorized Collaborators</h4>
                  <p className="text-secondary text-sm" style={{ marginBottom: '16px' }}>
                    Securely grant viewing or editing permissions to verified family members or colleagues.
                  </p>

                  <div className="share-add-bar">
                    <input
                      type="email"
                      className="form-input"
                      placeholder="collaborator@example.com"
                      value={shareRecipientEmail}
                      onChange={(e) => setShareRecipientEmail(e.target.value)}
                    />
                    <select
                      className="form-input"
                      style={{ width: '150px' }}
                      value={sharePermission}
                      onChange={(e) => setSharePermission(e.target.value)}
                    >
                      <option value="VIEW">Can View</option>
                      <option value="EDIT">Can Edit</option>
                    </select>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={handleAddShare}
                    >
                      <Plus size={15} />
                      <span>Add</span>
                    </button>
                  </div>

                  <div className="shares-list">
                    {sharesList.length === 0 ? (
                      <div className="shares-empty">
                        <Share2 size={32} opacity={0.3} />
                        <p>This identity record is private. Only you have decryption authorization.</p>
                      </div>
                    ) : (
                      <div className="shares-table">
                        {sharesList.map((sh) => (
                          <div key={sh.id} className="share-row">
                            <div className="share-email">
                              <strong>{sh.email}</strong>
                            </div>
                            <div className="share-perm-badge">
                              <span
                                className={`badge ${
                                  sh.permission === 'EDIT' ? 'badge-primary' : 'badge-secondary'
                                }`}
                              >
                                {sh.permission === 'EDIT' ? 'Can Edit' : 'Can View'}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="btn-icon btn-sm btn-icon-danger"
                              title="Revoke Access"
                              onClick={() => handleRemoveShare(sh.id)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Modal Footer with generous padding */}
          <div className="card-drawer-footer modal-footer identity-editor-footer">
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={onClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saving || !hasEditPermission || !title.trim()}
            >
              <Save size={14} />
              <span>{saving ? 'Encrypting & Storing...' : 'Save Identity'}</span>
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
